import { useState, useEffect, useCallback, useRef } from "react";
import { useTheme } from "../components/ThemeProvider";
import usePermiso from "../hooks/usePermiso";
import api from "../services/api";
import ModalWhatsApp from "../components/ModalWhatsApp";
import ModalEmail from "../components/ModalEmail";
import ModalPortales from "../components/ModalPortales";
import ModalImprimirPropiedades from "../components/propiedades/ModalImprimirPropiedades";
import ModalDetallePropiedad from "../components/ModalDetallePropiedad";
import FichaResumenPropiedad from "../components/propiedades/FichaResumenPropiedad";
import BadgeNoDisponible, { esNoDisponibleTemporal } from "../components/propiedades/BadgeNoDisponible";
import { abrirVistaClientePresentacion } from "../utils/abrirVistaClientePresentacion";

const API_ORIGIN =
  window.location.hostname === "localhost" ? "http://localhost:4000" : window.location.origin;

const OPS = ["Venta", "Alquiler"];
const HABS = ["1", "2", "3", "4", "5+"];
const BANOS = ["1", "2", "3", "4+"];
const ZONAS = ["Centro", "Las Marinas", "Montgó", "Rotas"];
const EXTRAS = [
  { k: "piscina", l: "🏊 Piscina" },
  { k: "garaje", l: "🚗 Garaje" },
  { k: "terraza", l: "☀️ Terraza" },
  { k: "ascensor", l: "🛗 Ascensor" },
  { k: "amueblado", l: "🪑 Amueblado" },
  { k: "primera_linea", l: "🏖️ 1ª línea" },
  { k: "vista_mar", l: "🌊 Vista mar" },
];
const EQUIP = {
  Almacenamiento: ["Trastero", "Jardín", "Armarios empotrados", "Lavadero", "Balcón", "Patio", "Solarium", "Tendedero", "Despensa"],
  Confort: ["Chimenea", "Suite", "Vestidor"],
  Seguridad: ["Domótica", "Video portero", "Puerta blindada", "Alarma"],
  Electrodomésticos: ["Cocina equipada", "Horno", "Lavavajillas", "Lavadora", "Secadora", "Frigorífico", "Microondas"],
  Exterior: ["Barbacoa", "Jacuzzi", "Sauna"],
  Comunitarios: ["Gimnasio", "Zona infantil", "Conserje", "Acceso minusválidos"],
};
const ORIENTACIONES = ["N", "S", "E", "O", "NE", "NO", "SE", "SO"];
const PLANTAS_OPCIONES = ["PB", "1º", "2º", "3º", "4º+"];
const CAL_ENERGETICAS = ["A", "B", "C", "D", "E", "F", "G"];

const CRITERIOS_INICIALES = {
  tipos: [],
  op: "",
  pMin: "",
  pMax: "",
  hMin: "",
  bMin: "",
  m2Min: "",
  m2Max: "",
  pobls: [],
  zonas: [],
  zonasExcl: [],
  extras: [],
  extrasExcl: [],
  equip: [],
  sinAsc: false,
  plMax: 2,
  m2Parcela: "",
  orientacion: [],
  orientacionExcl: [],
  jardin: false,
  anoConstruccion: "",
  estadoInmueble: [],
  estadoInmuebleExcl: [],
  vpo: false,
  vpoExcl: false,
  calEnergetica: "",
  plantas: [],
  plantasExcl: [],
  aireAc: false,
  calefaccion: false,
  padel: false,
  padelExcl: false,
  tenis: false,
  tenisExcl: false,
  puntoRecarga: false,
  adaptado: false,
  salidaHumos: false,
  distanciaMar: "",
  urbanizacionId: "",
};

const HEAD_INICIAL = {
  buscarTexto: "",
  captador: "",
  exclusiva: "todas",
  calificacion: "",
  fechaAltaDesde: "",
  fechaAltaHasta: "",
};

function fotoSrc(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${API_ORIGIN}${url}`;
}

function Chip({ label, active, onClick, small, theme, excluded }) {
  const c = theme.colors;
  const ex = excluded === true;
  const exBg = theme.name === "dark" ? "#991b1b" : "#dc2626";
  const border = ex ? "transparent" : active ? c.chipActiveBorder : c.chipBorder;
  const background = ex ? exBg : active ? c.chipActiveBg : c.chipBg;
  const color = ex ? "#ffffff" : active ? c.chipActiveText : c.chipText;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: small ? "2px 8px" : "4px 10px",
        fontSize: small ? 13 : 14,
        fontWeight: 500,
        borderRadius: 6,
        border: `1px solid ${border}`,
        background,
        color,
        cursor: "pointer",
        transition: "all 0.15s ease",
        userSelect: "none",
        lineHeight: "1.4",
      }}
      onMouseEnter={(e) => {
        if (ex || active) return;
        e.target.style.background = c.chipHoverBg;
        e.target.style.color = c.chipHoverText;
        e.target.style.borderColor = c.chipHoverBorder;
      }}
      onMouseLeave={(e) => {
        if (ex || active) return;
        e.target.style.background = c.chipBg;
        e.target.style.color = c.chipText;
        e.target.style.borderColor = c.chipBorder;
      }}
    >
      {label}
    </button>
  );
}

function propsParaModal(props, seleccionadas) {
  return seleccionadas
    .map((sid) => {
      const p = props.find((x) => x.id === sid);
      return p
        ? {
            id: p.id,
            titulo: p.titulo || "Sin título",
            detalle: `${p.tipo_nombre || p.tipo || ""} · ${p.poblacion_nombre || p.poblacion || ""}`,
            precio: p.precio ? `${Number(p.precio).toLocaleString("es-ES")} €` : "Consultar",
            icono: "🏠",
            tipo: p.tipo_nombre || p.tipo || "",
            poblacion: p.poblacion_nombre || p.poblacion || "",
            zona: p.zona_nombre || "",
            habitaciones: p.num_dormitorios || null,
            banos: p.num_banos || null,
            m2: p.m2_construidos || null,
            referencia: p.referencia || "",
            tenemos_llaves: p.tenemos_llaves || false,
            num_llave: p.num_llave || null,
            recoger_llaves_en: p.recoger_llaves_en || null,
          }
        : null;
    })
    .filter(Boolean);
}

export default function Buscador() {
  const { theme } = useTheme();
  const { puede } = usePermiso();
  const c = theme.colors;

  // ── Permisos granulares (permisosTree.js: nodo "buscador") ──
  const puedeVerResultados = puede("bus_resultados", "ver");
  const puedeVerBuscarTexto = puede("bus_buscar_texto", "ver");
  const puedeVerFiltroCaptador = puede("bus_filtro_captador", "ver");
  const puedeVerFiltroExclusiva = puede("bus_filtro_exclusiva", "ver");
  const puedeVerFiltroCalificacion = puede("bus_filtro_calificacion", "ver");
  const puedeVerFiltroFechaAlta = puede("bus_filtro_fecha_alta", "ver");
  const puedeVerCritOperacion = puede("bus_crit_operacion", "ver");
  const puedeVerCritTipo = puede("bus_crit_tipo", "ver");
  const puedeVerCritPrecio = puede("bus_crit_precio", "ver");
  const puedeVerCritHabs = puede("bus_crit_habs", "ver");
  const puedeVerCritBanos = puede("bus_crit_banos", "ver");
  const puedeVerCritM2 = puede("bus_crit_m2", "ver");
  const puedeVerCritPoblacion = puede("bus_crit_poblacion", "ver");
  const puedeVerCritZona = puede("bus_crit_zona", "ver");
  const puedeVerCritExtras = puede("bus_crit_extras", "ver");
  const puedeVerCritAvanzados = puede("bus_crit_avanzados", "ver");
  const puedeVerCritOrientacion = puede("bus_crit_orientacion", "ver");
  const puedeVerCritEstado = puede("bus_crit_estado", "ver");
  const puedeVerCritPlantas = puede("bus_crit_plantas", "ver");
  const puedeVerCritOtros = puede("bus_crit_otros", "ver");
  const puedeVerCritEquipamiento = puede("bus_crit_equipamiento", "ver");
  const puedeVerCritLimpiar = puede("bus_crit_limpiar", "ver");
  const puedeVerImprimir = puede("bus_imprimir", "ver");
  const puedeModificarImprimir = puede("bus_imprimir", "modificar");
  const puedeVerWhatsapp = puede("bus_whatsapp", "ver");
  const puedeModificarWhatsapp = puede("bus_whatsapp", "modificar");
  const puedeVerEmail = puede("bus_email", "ver");
  const puedeModificarEmail = puede("bus_email", "modificar");
  const puedeVerVistaCliente = puede("bus_vista_cliente", "ver");
  const puedeVerPortalesExternos = puede("bus_portales_externos", "ver");
  const puedeVerMasCriterios = puedeVerCritAvanzados || puedeVerCritM2 || puedeVerCritOrientacion || puedeVerCritEstado || puedeVerCritPlantas || puedeVerCritOtros || puedeVerCritEquipamiento;

  const [cr, setCr] = useState(CRITERIOS_INICIALES);
  const [head, setHead] = useState(HEAD_INICIAL);
  const [tiposStock, setTiposStock] = useState([]);
  const [poblacionesStock, setPoblacionesStock] = useState([]);
  const [estadosInmueble, setEstadosInmueble] = useState([]);
  const [masC, setMasC] = useState(false);
  const [props, setProps] = useState([]);
  const [propsExternas, setPropsExternas] = useState(null);
  const [propsExternasTipo, setPropsExternasTipo] = useState(null);
  const [propsExternasTotal, setPropsExternasTotal] = useState(0);
  const [propsExternasPage, setPropsExternasPage] = useState(1);
  const [propsExternasLoading, setPropsExternasLoading] = useState(false);
  const [modalPropExt, setModalPropExt] = useState(null);
  const [fichaResumenId, setFichaResumenId] = useState(null);
  const [modalWhatsApp, setModalWhatsApp] = useState(false);
  const [modalEmail, setModalEmail] = useState(false);
  const [modalPortales, setModalPortales] = useState(false);
  const [modalPrint, setModalPrint] = useState(false);
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [agentes, setAgentes] = useState([]);
  const criterioChipDblRef = useRef({ id: null, t: 0, timer: null });

  useEffect(() => {
    api.get("/agentes").then((r) => setAgentes(r.data.data || [])).catch(() => {});
    api.get("/auxiliares/tipos-con-stock").then((r) => setTiposStock(r.data?.data || [])).catch(() => {});
    api.get("/auxiliares/poblaciones-con-stock").then((r) => setPoblacionesStock(r.data?.data || [])).catch(() => {});
    api.get("/auxiliares/estado-inmueble").then((r) => {
      const rows = r.data?.data || [];
      setEstadosInmueble(rows.map((x) => x.nombre).filter(Boolean));
    }).catch(() => {});
  }, []);

  const buildQuery = useCallback((criterios, filtrosHead) => {
    const q = new URLSearchParams();
    if (criterios.tipos.length > 0) q.set("tipos", criterios.tipos.join(","));
    if (criterios.op) q.set("operacion", criterios.op);
    if (criterios.pMin) q.set("precio_min", criterios.pMin);
    if (criterios.pMax) q.set("precio_max", criterios.pMax);
    if (criterios.hMin) q.set("habs_min", criterios.hMin);
    if (criterios.bMin) q.set("banos_min", criterios.bMin);
    if (criterios.m2Min) q.set("m2_min", criterios.m2Min);
    if (criterios.m2Max) q.set("m2_max", criterios.m2Max);
    if (criterios.pobls.length > 0) q.set("poblaciones", criterios.pobls.join(","));
    if (criterios.zonas.length > 0) q.set("zonas", criterios.zonas.join(","));
    if (criterios.zonasExcl?.length > 0) q.set("zonas_excluidas", criterios.zonasExcl.join(","));
    if (criterios.extras.includes("piscina")) q.set("piscina", "true");
    if (criterios.extras.includes("garaje")) q.set("garaje", "true");
    if (criterios.extras.includes("terraza")) q.set("terraza", "true");
    if (criterios.extras.includes("ascensor")) q.set("ascensor", "true");
    if (criterios.extras.includes("amueblado")) q.set("amueblado", "true");
    if (criterios.extras.includes("primera_linea")) q.set("primera_linea", "true");
    if (criterios.extras.includes("vista_mar")) q.set("vista_mar", "true");
    if (criterios.extrasExcl?.includes("piscina")) q.set("piscina_excl", "true");
    if (criterios.extrasExcl?.includes("amueblado")) q.set("amueblado_excl", "true");
    if (criterios.m2Parcela) q.set("m2_parcela_min", criterios.m2Parcela);
    if (criterios.orientacion.length > 0) q.set("orientaciones", criterios.orientacion.join(","));
    if (criterios.orientacionExcl?.length > 0) q.set("orientaciones_excl", criterios.orientacionExcl.join(","));
    if (criterios.jardin) q.set("jardin", "true");
    if (criterios.anoConstruccion) q.set("ano_construccion_desde", criterios.anoConstruccion);
    if (criterios.estadoInmueble.length > 0) q.set("estado_inmueble", criterios.estadoInmueble.join(","));
    if (criterios.estadoInmuebleExcl?.length > 0) q.set("estado_inmueble_excl", criterios.estadoInmuebleExcl.join(","));
    if (criterios.vpo) q.set("vpo", "true");
    if (criterios.vpoExcl) q.set("vpo_excl", "true");
    if (criterios.calEnergetica) q.set("cal_energetica", criterios.calEnergetica);
    if (criterios.plantas.length > 0) q.set("plantas", criterios.plantas.join(","));
    if (criterios.plantasExcl?.length > 0) q.set("plantas_excl", criterios.plantasExcl.join(","));
    if (criterios.aireAc) q.set("aire_acondicionado", "true");
    if (criterios.calefaccion) q.set("calefaccion", "true");
    if (criterios.padel) q.set("padel", "true");
    if (criterios.padelExcl) q.set("padel_excl", "true");
    if (criterios.tenis) q.set("tenis", "true");
    if (criterios.tenisExcl) q.set("tenis_excl", "true");
    if (criterios.puntoRecarga) q.set("punto_recarga", "true");
    if (criterios.adaptado) q.set("adaptado", "true");
    if (criterios.salidaHumos) q.set("salida_humos", "true");
    if (criterios.distanciaMar) q.set("distancia_mar_max", criterios.distanciaMar);
    if (criterios.urbanizacionId) q.set("urbanizacion_id", criterios.urbanizacionId);
    if (criterios.sinAsc) q.set("sin_asc_planta", criterios.plMax);
    if (filtrosHead.captador) q.set("agente_id", filtrosHead.captador);
    if (filtrosHead.exclusiva && filtrosHead.exclusiva !== "todas") q.set("exclusiva", filtrosHead.exclusiva);
    if (filtrosHead.calificacion) q.set("calificacion", filtrosHead.calificacion);
    if (filtrosHead.fechaAltaDesde) q.set("fecha_alta_desde", filtrosHead.fechaAltaDesde);
    if (filtrosHead.fechaAltaHasta) q.set("fecha_alta_hasta", filtrosHead.fechaAltaHasta);
    const buscarTxt = String(filtrosHead.buscarTexto || "").trim().slice(0, 200);
    if (buscarTxt) q.set("buscar_general", buscarTxt);
    return q;
  }, []);

  const cargarProps = useCallback(
    async (criterios, filtrosHead) => {
      try {
        const q = buildQuery(criterios, filtrosHead);
        const r = await api.get(`/leads/propiedades/buscar?${q.toString()}`);
        setProps(r.data.data || []);
      } catch (e) {
        console.error("Error cargando propiedades:", e);
      }
    },
    [buildQuery],
  );

  const buscarPropsExternas = useCallback(async (tipo, criterios, page = 1) => {
    setPropsExternasLoading(true);
    try {
      const q = new URLSearchParams();
      q.set("limit", "100");
      q.set("page", String(page));
      if (tipo && tipo !== "todos") q.set("tipo_anunciante", tipo);
      if (criterios?.op) q.set("operacion", criterios.op.toLowerCase());
      if (criterios?.tipos?.length > 0) q.set("tipo_inmueble", criterios.tipos[0]);
      if (criterios?.pobls?.length > 0) q.set("poblacion", criterios.pobls.join(","));
      if (criterios?.pMin) q.set("precio_min", criterios.pMin);
      if (criterios?.pMax) q.set("precio_max", criterios.pMax);
      if (criterios?.hMin) q.set("hab_min", String(criterios.hMin).replace("+", ""));
      if (criterios?.bMin) q.set("banos_min", String(criterios.bMin).replace("+", ""));
      if (criterios?.m2Min) q.set("m2_min", criterios.m2Min);
      if (criterios?.m2Max) q.set("m2_max", criterios.m2Max);
      if (criterios?.extras?.includes("piscina")) q.set("piscina", "true");
      if (criterios?.extras?.includes("garaje")) q.set("garaje", "true");
      if (criterios?.extras?.includes("terraza")) q.set("terraza", "true");
      if (criterios?.extras?.includes("ascensor")) q.set("ascensor", "true");
      if (criterios?.extras?.includes("amueblado")) q.set("amueblado", "true");
      if (criterios?.extras?.includes("primera_linea") || criterios?.extras?.includes("1linea")) q.set("primera_linea", "true");
      if (criterios?.extras?.includes("vista_mar")) q.set("vista_mar", "true");
      if (criterios?.orientacion?.length > 0) q.set("orientacion", criterios.orientacion[0]);
      const res = await api.get(`/scraping/propiedades?${q.toString()}`);
      const items = res.data?.items || [];
      const total = res.data?.total || 0;
      if (page === 1) setPropsExternas(items);
      else setPropsExternas((prev) => [...(prev || []), ...items]);
      setPropsExternasTotal(total);
      setPropsExternasPage(page);
    } catch (err) {
      console.error("Error buscando en portales:", err);
      if (page === 1) setPropsExternas([]);
    } finally {
      setPropsExternasLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      cargarProps(cr, head);
    }, 400);
    return () => clearTimeout(timer);
  }, [cr, head, cargarProps]);

  useEffect(() => {
    if (propsExternasTipo === null) return;
    const timer = setTimeout(() => {
      buscarPropsExternas(propsExternasTipo, cr, 1);
    }, 400);
    return () => clearTimeout(timer);
  }, [cr, propsExternasTipo, buscarPropsExternas]);

  const tArr = (f, v) => {
    setCr((p) => {
      const a = p[f];
      return { ...p, [f]: a.includes(v) ? a.filter((x) => x !== v) : [...a, v] };
    });
  };

  const CRITERIO_DBL_MS = 380;
  const scheduleCriterioInclExcl = (id, onSingle, onDouble) => {
    const now = Date.now();
    const r = criterioChipDblRef.current;
    if (r.id === id && now - r.t < CRITERIO_DBL_MS) {
      if (r.timer) {
        clearTimeout(r.timer);
        r.timer = null;
      }
      r.id = null;
      r.t = 0;
      onDouble();
      return;
    }
    r.id = id;
    r.t = now;
    if (r.timer) clearTimeout(r.timer);
    r.timer = setTimeout(() => {
      r.timer = null;
      r.id = null;
      onSingle();
    }, CRITERIO_DBL_MS);
  };

  const onZonaChipPress = (z) => {
    scheduleCriterioInclExcl(
      `zona:${z}`,
      () =>
        setCr((p) => {
          const zo = p.zonas || [];
          const ex = p.zonasExcl || [];
          if (zo.includes(z)) return { ...p, zonas: zo.filter((x) => x !== z), zonasExcl: ex.filter((x) => x !== z) };
          return { ...p, zonas: [...zo, z], zonasExcl: ex.filter((x) => x !== z) };
        }),
      () =>
        setCr((p) => {
          const ex = p.zonasExcl || [];
          const zo = p.zonas || [];
          if (ex.includes(z)) return { ...p, zonasExcl: ex.filter((x) => x !== z) };
          return { ...p, zonas: zo.filter((x) => x !== z), zonasExcl: [...ex, z] };
        }),
    );
  };

  const onArrayInclExclChip = (inclKey, exclKey, val) => {
    scheduleCriterioInclExcl(
      `${String(inclKey)}:${String(val)}`,
      () =>
        setCr((p) => {
          const inc = p[inclKey] || [];
          const ex = p[exclKey] || [];
          if (inc.includes(val)) return { ...p, [inclKey]: inc.filter((x) => x !== val), [exclKey]: ex.filter((x) => x !== val) };
          return { ...p, [inclKey]: [...inc, val], [exclKey]: ex.filter((x) => x !== val) };
        }),
      () =>
        setCr((p) => {
          const ex = p[exclKey] || [];
          const inc = p[inclKey] || [];
          if (ex.includes(val)) return { ...p, [exclKey]: ex.filter((x) => x !== val) };
          return { ...p, [inclKey]: inc.filter((x) => x !== val), [exclKey]: [...ex, val] };
        }),
    );
  };

  const onExtraInclExclChip = (k) => {
    scheduleCriterioInclExcl(
      `extra:${k}`,
      () =>
        setCr((p) => {
          const inc = p.extras || [];
          const ex = p.extrasExcl || [];
          if (inc.includes(k)) return { ...p, extras: inc.filter((x) => x !== k), extrasExcl: ex.filter((x) => x !== k) };
          return { ...p, extras: [...inc, k], extrasExcl: ex.filter((x) => x !== k) };
        }),
      () =>
        setCr((p) => {
          const ex = p.extrasExcl || [];
          const inc = p.extras || [];
          if (ex.includes(k)) return { ...p, extrasExcl: ex.filter((x) => x !== k) };
          return { ...p, extras: inc.filter((x) => x !== k), extrasExcl: [...ex, k] };
        }),
    );
  };

  const onBoolTriChip = (inclField, exclField) => {
    scheduleCriterioInclExcl(
      `bool:${inclField}`,
      () => setCr((p) => ({ ...p, [inclField]: !p[inclField], [exclField]: false })),
      () => setCr((p) => ({ ...p, [inclField]: false, [exclField]: !p[exclField] })),
    );
  };

  const sF = (f, v) => setCr((p) => ({ ...p, [f]: v }));
  const s1 = (f, v) => setCr((p) => ({ ...p, [f]: p[f] === v ? "" : v }));
  const setHeadField = (f, v) => setHead((p) => ({ ...p, [f]: v }));

  const toggleSeleccion = (propId) => {
    setSeleccionadas((prev) => (prev.includes(propId) ? prev.filter((x) => x !== propId) : [...prev, propId]));
  };

  const limpiarCriterios = () => {
    setCr({ ...CRITERIOS_INICIALES });
    setHead({ ...HEAD_INICIAL });
    setSeleccionadas([]);
  };

  const inputStyle = {
    width: "100%",
    padding: "6px 10px",
    fontSize: 14,
    border: `1px solid ${c.border}`,
    borderRadius: 6,
    background: c.bgInput,
    color: c.textPrimary,
    outline: "none",
    transition: "border-color 0.15s",
  };

  const headSelectStyle = {
    padding: "5px 8px",
    fontSize: 14,
    borderRadius: 6,
    border: `1px solid ${c.border}`,
    background: c.bgInput,
    color: c.textPrimary,
    fontFamily: "inherit",
    outline: "none",
    minWidth: 0,
  };

  const sectionLabel = {
    fontSize: 16,
    fontWeight: 600,
    color: theme.name === "dark" ? c.textPrimary : "#1d4ed8",
    marginBottom: 4,
  };

  /** Título de acciones rápidas: mismo tamaño que antes el bloque de búsqueda (no tocar botones). */
  const accionesSectionLabel = { ...sectionLabel, fontSize: 13 };

  const sectionDivider = { borderBottom: `1px solid ${c.sectionBorder}` };

  const propsModal = propsParaModal(props, seleccionadas);

  return (
    <div style={{ display: "flex", height: "calc(100vh - 49px)", overflow: "hidden", background: c.bgApp }}>
      {/* PANEL IZQUIERDO */}
      <div
        style={{
          width: "50%",
          background: c.bgPanel,
          borderRight: `1px solid ${c.border}`,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        {/* CABECERA — Buscar, Captador, Exclusivas, Calificación, Fecha alta (una línea; scroll horizontal si falta sitio) */}
        <div style={{ padding: "10px 16px", ...sectionDivider }}>
          <div
            style={{
              display: "flex",
              flexWrap: "nowrap",
              gap: 8,
              alignItems: "flex-end",
              width: "100%",
              minWidth: 0,
              overflowX: "auto",
            }}
          >
            {puedeVerBuscarTexto && (
            <div style={{ flex: "2 1 160px", minWidth: 120, maxWidth: 280 }}>
              <div style={{ ...sectionLabel, fontSize: 14, marginBottom: 3 }}>Buscar</div>
              <input
                type="search"
                enterKeyHint="search"
                value={head.buscarTexto}
                onChange={(e) => setHeadField("buscarTexto", e.target.value)}
                placeholder="Ref., propietario, dirección, contactos…"
                title="Referencia, propietario, dirección, teléfono, email, contactos de la ficha"
                style={{
                  ...headSelectStyle,
                  width: "100%",
                  boxSizing: "border-box",
                  borderColor: head.buscarTexto?.trim() ? c.accentBlueBorder : c.border,
                  background: head.buscarTexto?.trim() ? c.accentBlueLight : c.bgInput,
                }}
              />
            </div>
            )}
            {puedeVerFiltroCaptador && (
            <div style={{ flex: "0 1 128px", minWidth: 100 }}>
              <div style={{ ...sectionLabel, fontSize: 14, marginBottom: 3 }}>Captador/a</div>
              <select
                value={head.captador}
                onChange={(e) => setHeadField("captador", e.target.value)}
                style={{
                  ...headSelectStyle,
                  width: "100%",
                  borderColor: head.captador ? c.accentBlueBorder : c.border,
                  background: head.captador ? c.accentBlueLight : c.bgInput,
                }}
              >
                <option value="">Todos</option>
                {agentes.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nombre}
                  </option>
                ))}
              </select>
            </div>
            )}
            {puedeVerFiltroExclusiva && (
            <div style={{ flex: "0 1 112px", minWidth: 96 }}>
              <div style={{ ...sectionLabel, fontSize: 14, marginBottom: 3 }}>Exclusivas</div>
              <select
                value={head.exclusiva}
                onChange={(e) => setHeadField("exclusiva", e.target.value)}
                style={{
                  ...headSelectStyle,
                  width: "100%",
                  borderColor: head.exclusiva !== "todas" ? "#f59e0b" : c.border,
                  background: head.exclusiva !== "todas" ? (theme.name === "dark" ? "rgba(245,158,11,0.12)" : "rgba(217,119,6,0.14)") : c.bgInput,
                }}
              >
                <option value="todas">Todas</option>
                <option value="si">Solo exclusivas</option>
                <option value="no">Sin exclusiva</option>
              </select>
            </div>
            )}
            {puedeVerFiltroCalificacion && (
            <div style={{ flex: "0 1 118px", minWidth: 96 }}>
              <div style={{ ...sectionLabel, fontSize: 14, marginBottom: 3 }}>Calificación</div>
              <select
                value={head.calificacion}
                onChange={(e) => setHeadField("calificacion", e.target.value)}
                style={{
                  ...headSelectStyle,
                  width: "100%",
                  borderColor: head.calificacion ? c.accentBlueBorder : c.border,
                  background: head.calificacion ? c.accentBlueLight : c.bgInput,
                }}
              >
                <option value="">Todas</option>
                <option value="sin">Sin calificar</option>
                <option value="0">0★ Chollo</option>
                <option value="1">1★ Adecuado</option>
                <option value="2">2★ Caro</option>
                <option value="3">3★ Fuera mercado</option>
              </select>
            </div>
            )}
            {puedeVerFiltroFechaAlta && (
            <div style={{ flex: "0 1 200px", minWidth: 168 }}>
              <div style={{ ...sectionLabel, fontSize: 14, marginBottom: 3 }}>Fecha alta</div>
              <div style={{ display: "flex", gap: 4, alignItems: "center", minWidth: 0 }}>
                <input
                  type="date"
                  value={head.fechaAltaDesde}
                  onChange={(e) => setHeadField("fechaAltaDesde", e.target.value)}
                  style={{ ...headSelectStyle, flex: 1, minWidth: 0 }}
                  title="Desde"
                />
                <span style={{ fontSize: 12, color: c.textMuted, flexShrink: 0 }}>—</span>
                <input
                  type="date"
                  value={head.fechaAltaHasta}
                  onChange={(e) => setHeadField("fechaAltaHasta", e.target.value)}
                  style={{ ...headSelectStyle, flex: 1, minWidth: 0 }}
                  title="Hasta"
                />
              </div>
            </div>
            )}
          </div>
        </div>

        {/* CRITERIOS DE BÚSQUEDA */}
        <div style={{ padding: "8px 16px", ...sectionDivider }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.name === "dark" ? c.textPrimary : "#1d4ed8" }}>
              Criterios de búsqueda
            </div>
            {puedeVerCritLimpiar && (
            <button
              type="button"
              title="Limpiar todos los filtros"
              onClick={limpiarCriterios}
              style={{
                fontSize: 13,
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: 4,
                cursor: "pointer",
                border: `1px solid ${c.border}`,
                background: "transparent",
                color: theme.name === "dark" ? c.textPrimary : "#1d4ed8",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              ✕ Limpiar
            </button>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {puedeVerCritOperacion && (
            <div>
              <div style={sectionLabel}>Operación</div>
              <div style={{ display: "flex", gap: 4 }}>
                {OPS.map((o) => (
                  <Chip key={o} label={o} active={cr.op.toLowerCase() === o.toLowerCase()} onClick={() => s1("op", o)} theme={theme} />
                ))}
              </div>
            </div>
            )}

            {puedeVerCritTipo && (
            <div>
              <div style={sectionLabel}>Tipo</div>
              {tiposStock.length === 0 ? (
                <span style={{ fontSize: 14, color: c.textMuted }}>Cargando tipos…</span>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {tiposStock.map((t) => (
                    <Chip key={t} label={t} active={cr.tipos.includes(t)} onClick={() => tArr("tipos", t)} theme={theme} />
                  ))}
                </div>
              )}
            </div>
            )}

            {(puedeVerCritPrecio || puedeVerCritHabs || puedeVerCritBanos) && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
              {puedeVerCritPrecio && (
              <>
              <div>
                <div style={sectionLabel}>Precio mín.</div>
                <input type="text" value={cr.pMin} onChange={(e) => sF("pMin", e.target.value)} style={{ ...inputStyle, padding: "5px 8px" }} placeholder="—" />
              </div>
              <div>
                <div style={sectionLabel}>Prec. máx.</div>
                <input type="text" value={cr.pMax} onChange={(e) => sF("pMax", e.target.value)} style={{ ...inputStyle, padding: "5px 8px" }} placeholder="—" />
              </div>
              </>
              )}
              {puedeVerCritHabs && (
              <div>
                <div style={sectionLabel}>Hab. mín.</div>
                <div style={{ display: "flex", gap: 3 }}>
                  {HABS.map((h) => (
                    <Chip key={h} label={h} small active={cr.hMin === h} onClick={() => s1("hMin", h)} theme={theme} />
                  ))}
                </div>
              </div>
              )}
              {puedeVerCritBanos && (
              <div>
                <div style={sectionLabel}>Baños mín.</div>
                <div style={{ display: "flex", gap: 3 }}>
                  {BANOS.map((b) => (
                    <Chip key={b} label={b} small active={cr.bMin === b} onClick={() => s1("bMin", b)} theme={theme} />
                  ))}
                </div>
              </div>
              )}
            </div>
            )}

            {puedeVerCritM2 && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <div>
                <div style={sectionLabel}>m² mín.</div>
                <input type="text" value={cr.m2Min} onChange={(e) => sF("m2Min", e.target.value)} style={{ ...inputStyle, padding: "5px 8px" }} placeholder="—" />
              </div>
              <div>
                <div style={sectionLabel}>m² máx.</div>
                <input type="text" value={cr.m2Max} onChange={(e) => sF("m2Max", e.target.value)} style={{ ...inputStyle, padding: "5px 8px" }} placeholder="—" />
              </div>
            </div>
            )}

            {puedeVerCritPoblacion && (
            <div>
              <div style={sectionLabel}>Población</div>
              {poblacionesStock.length === 0 ? (
                <span style={{ fontSize: 14, color: c.textMuted }}>Cargando poblaciones…</span>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {poblacionesStock.map((p) => (
                    <Chip key={p} label={p} active={cr.pobls.includes(p)} onClick={() => tArr("pobls", p)} theme={theme} />
                  ))}
                </div>
              )}
            </div>
            )}

            {puedeVerCritZona && (
            <div>
              <div style={sectionLabel}>Zona</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {ZONAS.map((z) => (
                  <Chip key={z} label={z} active={(cr.zonas || []).includes(z)} excluded={(cr.zonasExcl || []).includes(z)} onClick={() => onZonaChipPress(z)} theme={theme} />
                ))}
              </div>
            </div>
            )}

            {puedeVerCritExtras && (
            <div>
              <div style={sectionLabel}>Extras</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                {EXTRAS.map((e) => {
                  const inclExcl = e.k === "piscina" || e.k === "amueblado";
                  if (inclExcl) {
                    return (
                      <Chip
                        key={e.k}
                        label={e.l}
                        active={cr.extras.includes(e.k)}
                        excluded={(cr.extrasExcl || []).includes(e.k)}
                        onClick={() => onExtraInclExclChip(e.k)}
                        theme={theme}
                      />
                    );
                  }
                  return <Chip key={e.k} label={e.l} active={cr.extras.includes(e.k)} onClick={() => tArr("extras", e.k)} theme={theme} />;
                })}
                <Chip label="🛗 Sin ascensor hasta planta" active={cr.sinAsc} onClick={() => sF("sinAsc", !cr.sinAsc)} theme={theme} />
                {cr.sinAsc && (
                  <div style={{ display: "flex", alignItems: "center", borderRadius: 6, overflow: "hidden", border: `1px solid ${c.border}`, background: c.bgInput }}>
                    <button type="button" onClick={() => sF("plMax", Math.max(0, cr.plMax - 1))} style={{ width: 24, height: 24, border: "none", background: "transparent", cursor: "pointer", color: c.textMuted, fontSize: 15, fontWeight: 700 }}>▾</button>
                    <span style={{ width: 16, textAlign: "center", fontSize: 14, fontWeight: 700, color: c.textPrimary }}>{cr.plMax}</span>
                    <button type="button" onClick={() => sF("plMax", Math.min(10, cr.plMax + 1))} style={{ width: 24, height: 24, border: "none", background: "transparent", cursor: "pointer", color: c.textMuted, fontSize: 15, fontWeight: 700 }}>▴</button>
                  </div>
                )}
              </div>
            </div>
            )}

            {puedeVerMasCriterios && (
            <button
              type="button"
              onClick={() => setMasC(!masC)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
                width: "100%",
                padding: "6px 0",
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 700,
                border: `1px dashed ${c.border}`,
                background: "transparent",
                color: theme.name === "dark" ? c.textPrimary : "#1d4ed8",
                cursor: "pointer",
              }}
            >
              {masC ? "Menos criterios" : "+ Más criterios"}
            </button>
            )}

            {masC && puedeVerMasCriterios && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(puedeVerCritM2 || puedeVerCritAvanzados) && (
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                  {puedeVerCritM2 && (
                  <div>
                    <div style={sectionLabel}>m² Parcela mín.</div>
                    <input type="text" value={cr.m2Parcela} onChange={(e) => sF("m2Parcela", e.target.value)} style={{ ...inputStyle, padding: "5px 8px" }} placeholder="—" />
                  </div>
                  )}
                  {puedeVerCritAvanzados && (
                  <>
                  <div>
                    <div style={sectionLabel}>Año construido desde</div>
                    <input type="text" value={cr.anoConstruccion} onChange={(e) => sF("anoConstruccion", e.target.value)} style={{ ...inputStyle, padding: "5px 8px" }} placeholder="Ej: 2000" />
                  </div>
                  <div>
                    <div style={sectionLabel}>Dist. mar máx. (m)</div>
                    <input type="text" value={cr.distanciaMar} onChange={(e) => sF("distanciaMar", e.target.value)} style={{ ...inputStyle, padding: "5px 8px" }} placeholder="Ej: 500" />
                  </div>
                  <div>
                    <div style={sectionLabel}>Cal. Energética</div>
                    <select value={cr.calEnergetica} onChange={(e) => sF("calEnergetica", e.target.value)} style={{ ...inputStyle, padding: "5px 8px" }}>
                      <option value="">Cualquiera</option>
                      {CAL_ENERGETICAS.map((l) => (
                        <option key={l} value={l}>
                          {l}
                        </option>
                      ))}
                    </select>
                  </div>
                  </>
                  )}
                </div>
                )}

                {puedeVerCritOrientacion && (
                <div>
                  <div style={sectionLabel}>Orientación</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {ORIENTACIONES.map((o) => (
                      <Chip key={o} label={o} active={cr.orientacion.includes(o)} excluded={(cr.orientacionExcl || []).includes(o)} onClick={() => onArrayInclExclChip("orientacion", "orientacionExcl", o)} theme={theme} />
                    ))}
                  </div>
                </div>
                )}

                {puedeVerCritEstado && (
                <div>
                  <div style={sectionLabel}>Estado inmueble</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {estadosInmueble.map((e) => (
                      <Chip key={e} label={e} active={cr.estadoInmueble.includes(e)} excluded={(cr.estadoInmuebleExcl || []).includes(e)} onClick={() => onArrayInclExclChip("estadoInmueble", "estadoInmuebleExcl", e)} theme={theme} />
                    ))}
                  </div>
                </div>
                )}

                {puedeVerCritPlantas && (
                <div>
                  <div style={sectionLabel}>Plantas</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {PLANTAS_OPCIONES.map((pl) => (
                      <Chip key={pl} label={pl} active={cr.plantas.includes(pl)} excluded={(cr.plantasExcl || []).includes(pl)} onClick={() => onArrayInclExclChip("plantas", "plantasExcl", pl)} theme={theme} />
                    ))}
                  </div>
                </div>
                )}

                {puedeVerCritOtros && (
                <div>
                  <div style={sectionLabel}>Otros filtros</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    <Chip label="🌿 Jardín" active={cr.jardin} onClick={() => sF("jardin", !cr.jardin)} theme={theme} />
                    <Chip label="🏗️ VPO" active={cr.vpo} excluded={cr.vpoExcl} onClick={() => onBoolTriChip("vpo", "vpoExcl")} theme={theme} />
                    <Chip label="❄️ A.A." active={cr.aireAc} onClick={() => sF("aireAc", !cr.aireAc)} theme={theme} />
                    <Chip label="🔥 Calefacción" active={cr.calefaccion} onClick={() => sF("calefaccion", !cr.calefaccion)} theme={theme} />
                    <Chip label="🏸 Pista Pádel" active={cr.padel} excluded={cr.padelExcl} onClick={() => onBoolTriChip("padel", "padelExcl")} theme={theme} />
                    <Chip label="🎾 Pista Tenis" active={cr.tenis} excluded={cr.tenisExcl} onClick={() => onBoolTriChip("tenis", "tenisExcl")} theme={theme} />
                    <Chip label="🔌 Punto Recarga" active={cr.puntoRecarga} onClick={() => sF("puntoRecarga", !cr.puntoRecarga)} theme={theme} />
                    <Chip label="♿ Adaptado" active={cr.adaptado} onClick={() => sF("adaptado", !cr.adaptado)} theme={theme} />
                    <Chip label="💨 Salida Humos" active={cr.salidaHumos} onClick={() => sF("salidaHumos", !cr.salidaHumos)} theme={theme} />
                  </div>
                </div>
                )}

                {puedeVerCritEquipamiento && Object.entries(EQUIP).map(([cat, items]) => (
                  <div key={cat}>
                    <div style={sectionLabel}>{cat}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {items.map((i) => (
                        <Chip key={i} label={i} small active={cr.equip.includes(i)} onClick={() => tArr("equip", i)} theme={theme} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ACCIONES RÁPIDAS */}
        <div style={{ padding: "8px 16px" }}>
          <div style={accionesSectionLabel}>Acciones rápidas</div>
          <div style={{ display: "flex", gap: 6 }}>
            {[
              {
                l: "WhatsApp",
                bg: c.btnWhatsappBg,
                sh: c.btnWhatsappShadow,
                ic: (
                  <svg width={15} height={15} viewBox="0 0 24 24" fill="#fff">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                    <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" />
                  </svg>
                ),
                ver: puedeVerWhatsapp,
                modificar: puedeModificarWhatsapp,
                onClick: () => setModalWhatsApp(true),
              },
              {
                l: "Email",
                bg: c.btnEmailBg || "#b45309",
                sh: c.btnEmailShadow || "0 2px 6px rgba(180,83,9,0.25)",
                ic: (
                  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M22 7l-10 6L2 7" />
                  </svg>
                ),
                ver: puedeVerEmail,
                modificar: puedeModificarEmail,
                onClick: () => setModalEmail(true),
              },
              {
                l: "Imprimir",
                bg: "linear-gradient(135deg,#475569,#334155)",
                sh: "0 2px 6px rgba(71,85,105,0.3)",
                ic: (
                  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 6 2 18 2 18 9" />
                    <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
                    <rect x="6" y="14" width="12" height="8" />
                  </svg>
                ),
                ver: puedeVerImprimir,
                modificar: puedeModificarImprimir,
                onClick: () => setModalPrint(true),
              },
            ]
              .filter((a) => a.ver)
              .map((a) => (
              <button
                key={a.l}
                type="button"
                onClick={a.modificar ? a.onClick : undefined}
                disabled={!a.modificar}
                title={!a.modificar ? "No tienes permiso para usar esta acción" : undefined}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  padding: "8px 0",
                  borderRadius: 6,
                  cursor: a.modificar ? "pointer" : "not-allowed",
                  border: `1px solid ${c.btnActionBorder}`,
                  background: c.btnActionBg,
                  fontSize: 10,
                  fontWeight: 500,
                  color: c.btnActionText,
                  opacity: a.modificar ? 1 : 0.5,
                }}
              >
                <div style={{ width: 32, height: 32, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", background: a.bg, boxShadow: a.sh }}>{a.ic}</div>
                {a.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* PANEL DERECHO */}
      <div style={{ width: "50%", display: "flex", flexDirection: "column", background: c.bgPanel, minHeight: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 16px", borderBottom: `1px solid ${c.sectionBorder}` }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: c.textSecondary, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {propsExternasLoading ? (
              <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#0369a1" }}>Buscando...</span>
            ) : (
              <>
                <span style={{ color: c.accentBlue }}>{propsExternas !== null ? propsExternas.length : props.length}</span>
                {propsExternas !== null && propsExternasTotal > propsExternas.length && (
                  <span style={{ fontSize: 11, color: c.textMuted }}>de {propsExternasTotal.toLocaleString("es-ES")}</span>
                )}{" "}
                propiedades
              </>
            )}
            {propsExternas !== null && (
              <span style={{ fontSize: 11, fontWeight: 500, color: "#0369a1", background: "rgba(3,105,161,0.1)", padding: "2px 8px", borderRadius: 10 }}>
                🕷️ {propsExternasTipo === "todos" ? "Todos" : propsExternasTipo === "agencia" ? "Agencias" : "Particulares"}
              </span>
            )}
          </div>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {propsExternas !== null && (
              <button
                type="button"
                onClick={() => {
                  setPropsExternas(null);
                  setPropsExternasTipo(null);
                }}
                style={{ background: "rgba(3,105,161,0.15)", border: "1px solid rgba(3,105,161,0.3)", borderRadius: 6, cursor: "pointer", padding: "3px 10px", fontSize: 11, fontWeight: 600, color: "#0369a1" }}
              >
                ← CRM
              </button>
            )}
            {puedeVerPortalesExternos && (
            <button type="button" onClick={() => setModalPortales(true)} title="Buscar en portales externos" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, display: "flex" }}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={propsExternas !== null ? "#0ea5e9" : "#ffffff"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
            </button>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, color: c.liveColor }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.liveColor, animation: "pulse 2s infinite" }} />
            En vivo
          </div>
        </div>

        {seleccionadas.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: c.accentBlueLight || "rgba(37,99,235,0.1)", borderBottom: `1px solid ${c.sectionBorder}` }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: c.accentBlue, flex: 1 }}>
              {seleccionadas.length} seleccionada{seleccionadas.length !== 1 ? "s" : ""}
            </span>
            {puedeVerImprimir && (
            <button
              type="button"
              onClick={puedeModificarImprimir ? () => setModalPrint(true) : undefined}
              disabled={!puedeModificarImprimir}
              title={!puedeModificarImprimir ? "No tienes permiso para imprimir" : undefined}
              style={{ padding: "5px 12px", borderRadius: 7, border: "none", background: c.accentBlue, color: "#fff", fontSize: 12, fontWeight: 700, cursor: puedeModificarImprimir ? "pointer" : "not-allowed", opacity: puedeModificarImprimir ? 1 : 0.5 }}
            >
              Imprimir
            </button>
            )}
            {puedeVerWhatsapp && (
            <button
              type="button"
              onClick={puedeModificarWhatsapp ? () => setModalWhatsApp(true) : undefined}
              disabled={!puedeModificarWhatsapp}
              title={!puedeModificarWhatsapp ? "No tienes permiso para usar WhatsApp" : "WhatsApp"}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 12px",
                borderRadius: 7,
                border: "none",
                background: c.btnWhatsappBg,
                boxShadow: c.btnWhatsappShadow,
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                cursor: puedeModificarWhatsapp ? "pointer" : "not-allowed",
                opacity: puedeModificarWhatsapp ? 1 : 0.5,
              }}
            >
              <svg width={14} height={14} viewBox="0 0 24 24" fill="#fff" aria-hidden>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" />
              </svg>
              WhatsApp
            </button>
            )}
            <button type="button" onClick={() => setSeleccionadas([])} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${c.sectionBorder}`, background: "transparent", color: c.textMuted, fontSize: 11, cursor: "pointer" }}>
              ✕
            </button>
          </div>
        )}

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {!puedeVerResultados ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: c.textMuted, fontSize: 13 }}>
              No tienes permiso para ver los resultados de búsqueda
            </div>
          ) : propsExternas !== null ? (
            <div style={{ padding: 12, display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, alignContent: "start" }}>
              {propsExternas.length === 0 ? (
                <div style={{ gridColumn: "span 2", textAlign: "center", padding: "40px 0", color: c.textMuted }}>
                  <p style={{ fontWeight: 500, fontSize: 14, color: c.textSecondary }}>Sin resultados en portales</p>
                </div>
              ) : (
                propsExternas.map((p, idx) => {
                  const car = p.caracteristicas || {};
                  const hab = car.habitaciones ?? p.habitaciones;
                  const ban = car.banos ?? p.banos;
                  const m2 = car.superficie_m2 ?? p.superficie_m2;
                  const precioNum = p.precio_numerico || p.precio;
                  const foto0 = Array.isArray(p.fotos) && p.fotos.length > 0 ? p.fotos[0] : null;
                  return (
                    <div
                      key={p.id || idx}
                      onClick={() => setModalPropExt(p)}
                      style={{ background: c.propCardBg, borderRadius: 10, border: `1px solid ${c.propCardBorder}`, overflow: "hidden", cursor: "pointer" }}
                    >
                      <div style={{ position: "relative", aspectRatio: "16/9", background: c.bgCard }}>
                        {foto0 ? <img src={foto0} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                        <div style={{ position: "absolute", bottom: 8, left: 8, background: c.propBadgeBg, color: c.propBadgeText, padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                          {precioNum ? `${Number(precioNum).toLocaleString("es-ES")} €` : "Sin precio"}
                        </div>
                      </div>
                      <div style={{ padding: 10 }}>
                        <h3 style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.titulo || "Sin título"}</h3>
                        <p style={{ fontSize: 11, color: c.textMuted, margin: "2px 0 0" }}>{p.municipio || p.direccion || "—"}</p>
                        <div style={{ display: "flex", gap: 10, marginTop: 6, fontSize: 11, color: c.textSecondary }}>
                          {hab > 0 && <span>🛏 {hab} hab</span>}
                          {ban > 0 && <span>🚿 {ban}</span>}
                          {m2 > 0 && <span>📐 {Math.round(m2)} m²</span>}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              {propsExternas !== null && propsExternasTotal > propsExternas.length && (
                <div style={{ gridColumn: "span 2", padding: "12px 0", display: "flex", justifyContent: "center" }}>
                  <button type="button" onClick={() => buscarPropsExternas(propsExternasTipo, cr, propsExternasPage + 1)} disabled={propsExternasLoading} style={{ padding: "8px 24px", borderRadius: 8, border: `1px solid ${c.sectionBorder}`, background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#0369a1" }}>
                    {propsExternasLoading ? "Cargando..." : `Cargar más (${propsExternas.length} / ${propsExternasTotal.toLocaleString("es-ES")})`}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: 12, display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, alignContent: "start" }}>
              {props.length === 0 ? (
                <div style={{ gridColumn: "span 2", display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: c.textMuted }}>
                  <div style={{ textAlign: "center" }}>
                    <p style={{ fontWeight: 500, fontSize: 14, color: c.textSecondary }}>No hay propiedades</p>
                    <p style={{ fontSize: 12, marginTop: 4 }}>Ajusta los criterios de búsqueda</p>
                  </div>
                </div>
              ) : (
                props.map((p) => {
                  const estaSeleccionada = seleccionadas.includes(p.id);
                  const noDisponible = esNoDisponibleTemporal(p);
                  return (
                    <div
                      key={p.id}
                      style={{
                        background: c.propCardBg,
                        borderRadius: 10,
                        border: `1px solid ${estaSeleccionada ? c.accentBlue : c.propCardBorder}`,
                        overflow: "hidden",
                        cursor: "pointer",
                        position: "relative",
                      }}
                    >
                      <div style={{ position: "relative", aspectRatio: "16/9", background: c.bgCard }} onClick={() => setFichaResumenId(p.id)}>
                        {p.fotos && p.fotos.length > 0 ? (
                          <img src={fotoSrc(p.fotos[0])} alt={p.titulo} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: c.textPlaceholder }}>
                            <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <path d="M21 15l-5-5L5 21" />
                            </svg>
                          </div>
                        )}

                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSeleccion(p.id);
                          }}
                          style={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            zIndex: 6,
                            width: 22,
                            height: 22,
                            borderRadius: 5,
                            border: `2px solid ${estaSeleccionada ? "#fff" : "rgba(255,255,255,0.8)"}`,
                            background: estaSeleccionada ? c.accentBlue : "rgba(0,0,0,0.25)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          {estaSeleccionada && (
                            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>

                        {puedeVerVistaCliente && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirVistaClientePresentacion(p.id);
                          }}
                          title="Vista cliente (TV)"
                          style={{
                            position: "absolute",
                            top: 36,
                            left: 8,
                            zIndex: 6,
                            width: 22,
                            height: 22,
                            borderRadius: 5,
                            border: "2px solid rgba(255,255,255,0.85)",
                            background: "rgba(0,0,0,0.35)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="5" width="16" height="11" rx="1.5" />
                            <path d="M8 21h8" />
                            <path d="M12 16v5" />
                          </svg>
                        </div>
                        )}

                        {noDisponible && (
                          <BadgeNoDisponible
                            disponibleDesde={p.disponible_desde}
                            motivo={p.motivo_no_disponible}
                          />
                        )}

                        <div style={{ position: "absolute", bottom: 8, left: 8, background: c.propBadgeBg, color: c.propBadgeText, padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                          {p.precio_web_consultar ? "A consultar" : p.precio_venta || p.precio ? `${Number(p.precio_venta || p.precio).toLocaleString("es-ES")} €` : "Sin precio"}
                        </div>
                        <div style={{ position: "absolute", top: 8, right: 8, padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, color: "#fff", background: p.operacion?.toLowerCase() === "alquiler" ? c.accentPink : c.accentGreen }}>
                          {p.operacion || "Venta"}
                        </div>
                      </div>

                      <div style={{ padding: 12 }} onClick={() => setFichaResumenId(p.id)}>
                        <h3 style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.titulo || "Sin título"}</h3>
                        <p style={{ fontSize: 11, color: c.textMuted, margin: "2px 0 0" }}>
                          {p.tipo_nombre || p.tipo || ""} · {p.poblacion_nombre || p.poblacion || ""}
                          {p.zona_nombre ? ` · ${p.zona_nombre}` : ""}
                        </p>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, fontSize: 11, color: c.textSecondary }}>
                          {p.m2_construidos > 0 && <span>📐 {Math.round(p.m2_construidos)} m²</span>}
                          {p.num_dormitorios > 0 && <span>🛏 {p.num_dormitorios} hab</span>}
                          {p.num_banos > 0 && <span>🚿 {p.num_banos} {p.num_banos === 1 ? "baño" : "baños"}</span>}
                          {p.num_aseos > 0 && <span>🚽 {p.num_aseos} {p.num_aseos === 1 ? "aseo" : "aseos"}</span>}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, paddingTop: 8, borderTop: `1px solid ${c.borderLight}` }}>
                          <span style={{ fontSize: 10, color: c.textMuted }}>
                            Ref: <b style={{ color: c.textSecondary }}>{p.referencia || "—"}</b>
                          </span>
                          {p.agente_nombre && <span style={{ fontSize: 10, color: c.textMuted }}>{p.agente_nombre}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {modalPropExt && <ModalDetallePropiedad p={modalPropExt} theme={theme} onClose={() => setModalPropExt(null)} />}
      {fichaResumenId && <FichaResumenPropiedad propiedadId={fichaResumenId} onClose={() => setFichaResumenId(null)} />}

      {modalWhatsApp && (
        <ModalWhatsApp
          isOpen={modalWhatsApp}
          onClose={() => setModalWhatsApp(false)}
          onSend={() => setModalWhatsApp(false)}
          allowManualRecipient
          contact={{ nombre: "", telefono: "", iniciales: "?" }}
          properties={propsModal}
        />
      )}

      {modalEmail && (
        <ModalEmail
          isOpen={modalEmail}
          onClose={() => setModalEmail(false)}
          onSend={() => setModalEmail(false)}
          allowManualRecipient
          contact={{ nombre: "", email: "", iniciales: "?" }}
          properties={propsModal}
        />
      )}

      {modalPortales && (
        <ModalPortales
          onClose={() => setModalPortales(false)}
          criterios={cr}
          onSeleccionar={(tipo) => {
            setPropsExternasTipo(tipo);
            buscarPropsExternas(tipo, cr, 1);
          }}
        />
      )}

      {modalPrint && (
        <ModalImprimirPropiedades
          isOpen={modalPrint}
          onClose={() => setModalPrint(false)}
          propiedades={seleccionadas.map((sid) => props.find((x) => x.id === sid)).filter(Boolean)}
        />
      )}

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}
